import React, { useState } from 'react';
import { Upload, Button, Table, Tag, Alert,
         message, Card, Row, Col, Statistic } from 'antd';
import { InboxOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';

const { Dragger } = Upload;

// ── Types silently stripped — metadata, never shown to users ──────────────────
const SILENT_STRIP = new Set([
    'start','end','today','deviceid','phonenumber','username',
    'audit','calculate','hidden',
]);

// ── XLSForm type → our question type ─────────────────────────────────────────
const TYPE_MAP = {
    'text':        'text',
    'string':      'text',
    'integer':     'number',
    'decimal':     'number',
    'number':      'number',
    'range':       'scale',
    'scale':       'scale',
    'date':        'date',
    'time':        'text',
    'datetime':    'text',
    'acknowledge': 'boolean',
    'boolean':     'boolean',
    'note':        'textarea',
    // location → text (coordinates stored as string)
    'geopoint':    'text',
    'geotrace':    'text',
    'geoshape':    'text',
    // file uploads
    'image':       'file',
    'audio':       'file',
    'video':       'file',
    'file':        'file',
    // rank → multi-select
    'rank':        'multiple',
    'barcode':     'text',
    'uri':         'text',
};

const getSelectType = type => {
    if (type.startsWith('select_one'))      return 'single';
    if (type.startsWith('select_multiple')) return 'multiple';
    return null;
};

const getListName = type => {
    const parts = type.split(' ');
    return parts.length > 1 ? parts[1] : null;
};

const parseRelevant = relevant => {
    if (!relevant) return null;
    const match = relevant.match(/\$\{([^}]+)\}\s*(!=|=)\s*'?([^'\s)]*)'?/);
    if (match) return { field: match[1], operator: match[2] === '!=' ? '!=' : '=', value: match[3] };
    return null;
};

// ── Max flattened instances per repeat group ──────────────────────────────────
const REPEAT_MAX = 3;

// ── Main XLSForm parser ───────────────────────────────────────────────────────
const parseXLSForm = (workbook) => {
    const result = {
        title:     '',
        sections:  [],
        questions: [],
        choices:   {},
        warnings:  [],
        skipped:   [],
        stats:     { total: 0, mapped: 0, skipped: 0, sections: 0, stripped: 0 },
    };

    // Settings sheet
    if (workbook.SheetNames.includes('settings')) {
        const settings = XLSX.utils.sheet_to_json(workbook.Sheets['settings']);
        if (settings[0]) result.title = settings[0]['form_title'] || settings[0]['title'] || '';
    }

    // Choices sheet
    if (workbook.SheetNames.includes('choices')) {
        XLSX.utils.sheet_to_json(workbook.Sheets['choices']).forEach(row => {
            const listName = row['list_name'] || row['list name'];
            const name     = String(row['name'] || '');
            const label    = String(row['label'] || row['label::English'] || row['label::English (en)'] || row['label::english'] || name);
            if (listName && name) {
                if (!result.choices[listName]) result.choices[listName] = [];
                result.choices[listName].push({ value: name, label });
            }
        });
    }

    // Survey sheet
    if (!workbook.SheetNames.includes('survey')) {
        result.warnings.push('No survey sheet found in this file');
        return result;
    }

    const rows = XLSX.utils.sheet_to_json(workbook.Sheets['survey'], { defval: '' });

    let sectionId    = 's_default';
    let sectionTitle = 'General';
    const repeatStack = []; // stack for nested repeat groups

    // Add default section
    result.sections.push({ id: sectionId, title: sectionTitle, description: '' });
    result.stats.sections++;

    // Helper: resolve mapped type + options for a raw XLSForm type string
    const resolveType = (type, name, label) => {
        let mappedType = TYPE_MAP[type] ?? null;
        let options    = [];
        const isGeo   = type === 'geopoint' || type === 'geotrace' || type === 'geoshape';

        if (!mappedType) {
            mappedType = getSelectType(type);
            if (mappedType) {
                const listName = getListName(type);
                if (listName) {
                    options = (result.choices[listName] || []).map(c => c.label || c.value);
                    if (options.length === 0) {
                        result.warnings.push(`Choice list "${listName}" not found for "${label || name}"`);
                    }
                }
            }
        }
        return { mappedType, options, isGeo };
    };

    // Helper: build a question object
    const buildQ = ({ idx, mappedType, name, label, hint, req, relevant, options, isGeo, repeatGroupLabel, instanceNum }) => ({
        id:              'q_' + Date.now() + '_' + idx + (instanceNum ? '_' + instanceNum : ''),
        section_id:      sectionId,
        type:            mappedType,
        label:           repeatGroupLabel && instanceNum !== undefined
                             ? `${repeatGroupLabel} ${instanceNum} — ${label || name}`
                             : (label || name),
        helper_text:     hint + (isGeo ? ' (GPS coordinates — latitude,longitude)' : ''),
        required:        instanceNum === 1
                             ? (req === 'yes' || req === 'true' || req === '1')
                             : false,   // only first instance required
        options:         options || [],
        rows:            [],
        columns:         ['Yes', 'No'],
        scale_min:       1,
        scale_max:       5,
        skip_logic:      parseRelevant(relevant),
        xlsform_name:    name,
        xlsform_type:    repeatGroupLabel ? `repeat:${name}` : name,
        repeat_group:    repeatGroupLabel || null,
        repeat_instance: instanceNum     || null,
    });

    rows.forEach((row, idx) => {
        const type     = String(row['type']     || '').trim().toLowerCase();
        const name     = String(row['name']     || '').trim();
        const label    = String(row['label'] || row['label::English'] || row['label::English (en)'] || row['label::english'] || name).trim();
        const hint     = String(row['hint']     || '').trim();
        const req      = String(row['required'] || '').trim().toLowerCase();
        const relevant = String(row['relevant'] || '').trim();

        if (!type || !name) return;

        // ── Groups → sections (outside repeats only) ──────────────────────
        if (type === 'begin_group' || type === 'begin group') {
            if (repeatStack.length === 0) {
                sectionId    = 's_' + name;
                sectionTitle = label || name;
                result.sections.push({ id: sectionId, title: sectionTitle, description: hint });
                result.stats.sections++;
            }
            return;
        }
        if (type === 'end_group' || type === 'end group') return;

        // ── Repeat begin — push onto stack ────────────────────────────────
        if (type === 'begin_repeat' || type === 'begin repeat') {
            repeatStack.push({ name, label: label || name, fields: [] });
            return;
        }

        // ── Repeat end — flatten buffered fields into numbered questions ───
        if (type === 'end_repeat' || type === 'end repeat') {
            if (repeatStack.length === 0) return;
            const group = repeatStack.pop();
            if (group.fields.length === 0) return;

            // Create a dedicated section for this repeat group
            const repeatSectionId = 's_repeat_' + group.name;
            const prevSectionId   = sectionId;
            sectionId = repeatSectionId;

            result.sections.push({
                id:         repeatSectionId,
                title:      group.label,
                description:`Add up to ${REPEAT_MAX} entries`,
                is_repeat:  true,
                repeat_max: REPEAT_MAX,
            });
            result.stats.sections++;

            // Flatten: generate instance 1..REPEAT_MAX for every field
            for (let i = 1; i <= REPEAT_MAX; i++) {
                group.fields.forEach((f, fi) => {
                    result.questions.push(buildQ({
                        idx:              idx * 1000 + fi * 10 + i,
                        mappedType:       f.mappedType,
                        name:             f.name,
                        label:            f.label,
                        hint:             f.hint,
                        req:              f.req,
                        relevant:         f.relevant,
                        options:          f.options,
                        isGeo:            f.isGeo,
                        repeatGroupLabel: group.label,
                        instanceNum:      i,
                    }));
                    result.stats.mapped++;
                });
            }

            sectionId = prevSectionId;
            return;
        }

        // ── Inside a repeat group — buffer the field ──────────────────────
        if (repeatStack.length > 0) {
            result.stats.total++;
            if (SILENT_STRIP.has(type)) { result.stats.stripped++; return; }

            const { mappedType, options, isGeo } = resolveType(type, name, label);
            const finalType = mappedType || 'text'; // fallback unknown → text
            if (!mappedType) {
                result.warnings.push(`Unknown type "${type}" for "${label}" in repeat group — imported as text`);
            }
            repeatStack[repeatStack.length - 1].fields.push({ name, label, hint, req, relevant, mappedType: finalType, options, isGeo });
            return;
        }

        // ── Normal question ───────────────────────────────────────────────
        result.stats.total++;

        // Silently strip metadata
        if (SILENT_STRIP.has(type)) { result.stats.stripped++; return; }

        const { mappedType, options, isGeo } = resolveType(type, name, label);

        if (!mappedType) {
            result.skipped.push({ name, label: label || name, reason: `Unknown type: ${type}` });
            result.stats.skipped++;
            return;
        }

        result.questions.push(buildQ({ idx, mappedType, name, label, hint, req, relevant, options, isGeo }));
        result.stats.mapped++;
    });

    // Warn about unclosed repeat groups (malformed XLSForm)
    repeatStack.forEach(g => {
        result.warnings.push(`Repeat group "${g.label}" was never closed — some fields may be missing`);
    });

    return result;
};

// ── UI Component (unchanged from original) ────────────────────────────────────
export default function QoCSurveyImport({ onImport, onClose }) {
    const [parsed,   setParsed]   = useState(null);
    const [loading,  setLoading]  = useState(false);
    const [fileName, setFileName] = useState('');

    const handleFile = (file) => {
        setLoading(true);
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const wb   = XLSX.read(data, { type: 'array' });
                const result = parseXLSForm(wb);
                setParsed(result);
            } catch(err) {
                message.error('Failed to parse XLSForm: ' + err.message);
            } finally {
                setLoading(false);
            }
        };
        reader.readAsArrayBuffer(file);
        return false;
    };

    const handleImport = () => {
        if (!parsed) return;
        onImport({
            title:     parsed.title || fileName.replace('.xlsx','').replace('.xls',''),
            sections:  parsed.sections,
            questions: parsed.questions,
        });
    };

    const qCols = [
        { title:'#', width:40, render:(_,__,i)=>i+1 },
        { title:'Label', dataIndex:'label', ellipsis:true,
            render:(v,r) => (
                <span>
                    {r.repeat_group && <Tag color="purple" style={{marginRight:4}}>Repeat {r.repeat_instance}</Tag>}
                    {r.xlsform_type === r.xlsform_name && r.type === 'text' && r.helper_text?.includes('GPS') &&
                        <Tag color="cyan" style={{marginRight:4}}>GPS</Tag>}
                    {v}
                </span>
            )
        },
        { title:'XLSForm Type', dataIndex:'xlsform_type', width:140, render: v => <Tag>{v}</Tag> },
        { title:'Mapped To',    dataIndex:'type',          width:100, render: v => <Tag color="blue">{v}</Tag> },
        { title:'Section',      dataIndex:'section_id',    width:130, ellipsis:true },
        { title:'Options', dataIndex:'options', width:70,
            render: v => v?.length ? <Tag color="green">{v.length}</Tag> : null },
        { title:'Req', dataIndex:'required', width:50,
            render: v => v ? <CheckCircleOutlined style={{color:'#16a34a'}}/> : null },
    ];

    const skipCols = [
        { title:'Name',   dataIndex:'name' },
        { title:'Label',  dataIndex:'label', ellipsis:true },
        { title:'Reason', dataIndex:'reason', render: v => <Tag color="orange">{v}</Tag> },
    ];

    // Friendly summary of what was stripped silently
    const strippedCount = parsed?.stats?.stripped || 0;

    return (
        <div>
            {!parsed ? (
                <Dragger beforeUpload={handleFile} accept=".xlsx,.xls" showUploadList={false} disabled={loading}>
                    <p className="ant-upload-drag-icon"><InboxOutlined/></p>
                    <p className="ant-upload-text">Click or drag an XLSForm (.xlsx) file here</p>
                    <p className="ant-upload-hint">
                        Supports groups, repeat groups (flattened to {REPEAT_MAX} instances),
                        select_one, select_multiple, text, number, date, boolean, geopoint,
                        file uploads, range/scale. Metadata fields (start, end, deviceid, etc.)
                        are stripped automatically.
                    </p>
                </Dragger>
            ) : (
                <div>
                    {/* Stats */}
                    <Row gutter={12} style={{marginBottom:16}}>
                        <Col span={6}><Card size="small">
                            <Statistic title="Questions Found" value={parsed.stats.total} valueStyle={{color:'#1890ff',fontWeight:800}}/>
                        </Card></Col>
                        <Col span={6}><Card size="small">
                            <Statistic title="Will Import" value={parsed.stats.mapped} valueStyle={{color:'#16a34a',fontWeight:800}}/>
                        </Card></Col>
                        <Col span={6}><Card size="small">
                            <Statistic title="Sections" value={parsed.stats.sections} valueStyle={{color:'#7c3aed',fontWeight:800}}/>
                        </Card></Col>
                        <Col span={6}><Card size="small">
                            <Statistic title="Skipped" value={parsed.stats.skipped} valueStyle={{color:parsed.stats.skipped>0?'#d97706':'#aaa',fontWeight:800}}/>
                        </Card></Col>
                    </Row>

                    {/* Silent strip notice */}
                    {strippedCount > 0 && (
                        <Alert type="info" showIcon style={{marginBottom:8}}
                            message={`${strippedCount} metadata field${strippedCount>1?'s':''} (start, end, deviceid, calculate, etc.) stripped automatically — these are never shown to users.`}/>
                    )}

                    {/* Repeat group notice */}
                    {parsed.sections.some(s=>s.is_repeat) && (
                        <Alert type="info" showIcon style={{marginBottom:8}}
                            message={`Repeat groups flattened: each field is imported as up to ${REPEAT_MAX} numbered instances (e.g. "Reviewer 1 — Name", "Reviewer 2 — Name"). The form UI will show an Add/Remove interface.`}/>
                    )}

                    {/* Warnings */}
                    {parsed.warnings.map((w,i)=>(
                        <Alert key={i} type="warning" showIcon message={w} style={{marginBottom:8}}/>
                    ))}

                    {/* Questions preview */}
                    <Card size="small" style={{marginBottom:12}}
                        title={<span><CheckCircleOutlined style={{color:'#16a34a'}}/> Questions to Import ({parsed.questions.length})</span>}>
                        <Table size="small" dataSource={parsed.questions} rowKey="id"
                            columns={qCols} pagination={{pageSize:10,size:'small'}}
                            rowClassName={r => r.repeat_group ? 'ant-table-row-selected' : ''}/>
                    </Card>

                    {/* Skipped — only real unknowns, not metadata */}
                    {parsed.skipped.length > 0 && (
                        <Card size="small" style={{marginBottom:12}}
                            title={<span><WarningOutlined style={{color:'#d97706'}}/> Skipped — Unknown Types ({parsed.skipped.length})</span>}>
                            <Table size="small" dataSource={parsed.skipped} rowKey="name"
                                columns={skipCols} pagination={{pageSize:5,size:'small'}}/>
                        </Card>
                    )}

                    {/* Actions */}
                    <div style={{display:'flex',gap:8,marginTop:16,justifyContent:'flex-end'}}>
                        <Button onClick={()=>{setParsed(null);setFileName('');}}>← Upload Different File</Button>
                        <Button type="primary" icon={<CheckCircleOutlined/>}
                            onClick={handleImport} disabled={parsed.questions.length===0}>
                            Import {parsed.questions.length} Questions
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}